import { useState, useMemo } from "react";
import { num, m2, money, UNITS, CalcLayout, FormCol, Card, Field, Sel, HowTo, Advanced, AreaTable, sumArea, ResultCard, useCatalog, ProductSelect } from "./shared";

const SUP_FACTOR = { "Lisa": 1.0, "Ligeramente porosa": 0.9, "Porosa": 0.8, "Muy porosa": 0.7, "Rugosa": 0.75 };
const PRESET = [["cubeta", "Cubeta 19 L", 19], ["galon", "Galón 4 L", 4], ["litro", "Litro 1 L", 1]];

export default function Pintura({ onSave, s }) {
  const items = useCatalog("pintura");
  const [pid, setPid] = useState("");
  const prod = items.find((p) => p.id === pid);
  const [u, setU] = useState("m");
  const [tipo, setTipo] = useState("Vinílica");
  const [superficie, setSuperficie] = useState("Lisa");
  const [muros, setMuros] = useState([{ nombre: "Muro 1", largo: "4.50", ancho: "2.70", cant: "1" }]);
  const [descuentos, setDescuentos] = useState([]);
  const [manos, setManos] = useState("2");
  const [rendTeorico, setRendTeorico] = useState("10");
  const [desp, setDesp] = useState("8");
  const [precioLitro, setPrecioLitro] = useState("");
  const [precioInst, setPrecioInst] = useState("");
  const [selladorManos, setSelladorManos] = useState("0");
  const [selladorRend, setSelladorRend] = useState("8");
  const pr = prod ? prod.price : num(precioLitro);

  const r = useMemo(() => {
    const areaBruta = sumArea(muros, u), areaDesc = sumArea(descuentos, u);
    const areaNeta = Math.max(0, areaBruta - areaDesc);
    const factor = SUP_FACTOR[superficie] ?? 1;
    const rendReal = num(rendTeorico) * factor;
    const areaAplic = areaNeta * Math.max(1, Math.round(num(manos)) || 1);
    const litrosTeoricos = rendReal > 0 ? areaAplic / rendReal : 0;
    const litrosAjust = litrosTeoricos * (1 + num(desp) / 100);
    // Optimización de envases (menor sobrante)
    let restante = litrosAjust, combo = [], litrosComprados = 0;
    PRESET.forEach(([, label, cap]) => {
      const n = Math.floor(restante / cap);
      if (n > 0) { combo.push(`${n} × ${label}`); restante -= n * cap; litrosComprados += n * cap; }
    });
    if (restante > 0.01) { combo.push("1 × Litro 1 L"); litrosComprados += 1; restante = 0; }
    const sobrante = litrosComprados - litrosAjust;
    // Sellador
    const selM = Math.max(0, Math.round(num(selladorManos)) || 0);
    const litrosSell = selM > 0 && num(selladorRend) > 0 ? (areaNeta * selM / num(selladorRend)) * (1 + num(desp) / 100) : 0;
    const cMat = litrosComprados * pr;
    const total = cMat + num(precioInst);
    const w = [];
    if (rendReal <= 0) w.push("Captura un rendimiento válido del producto.");
    if (areaNeta === 0) w.push("Captura al menos un muro con medidas.");
    if (superficie !== "Lisa") w.push(`Superficie ${superficie}: rendimiento ajustado al ${(factor * 100).toFixed(0)}%.`);
    if (prod && prod.stock < litrosComprados) w.push(`Inventario insuficiente de "${prod.name}": stock ${prod.stock} L, requieres ${litrosComprados} L.`);
    const rows = [
      { section: "Proyecto" },
      { label: "Tipo de pintura", value: tipo }, { label: "Superficie", value: superficie },
      { label: "Área bruta", value: m2(areaBruta) }, { label: "Área descontada", value: m2(areaDesc) },
      { label: "Área neta", value: m2(areaNeta) }, { label: "Número de manos", value: `${Math.max(1, Math.round(num(manos)) || 1)}` },
      { label: "Área total de aplicación", value: m2(areaAplic) },
      { section: "Cálculo" },
      { label: "Rendimiento teórico", value: `${num(rendTeorico)} m²/L` }, { label: "Factor de rendimiento", value: `${(factor * 100).toFixed(0)}%` },
      { label: "Rendimiento real", value: `${rendReal.toFixed(2)} m²/L` }, { label: "Litros teóricos", value: `${litrosTeoricos.toFixed(2)} L` },
      { label: "Desperdicio aplicado", value: `${num(desp)}%` }, { label: "Litros ajustados", value: `${litrosAjust.toFixed(2)} L`, strong: true },
      { section: "Presentaciones (menor sobrante)" },
      { label: "Combinación", value: combo.join(" + ") || "—" }, { label: "Litros comprados", value: `${litrosComprados.toFixed(2)} L` },
      { label: "Sobrante estimado", value: `${sobrante.toFixed(2)} L` },
      ...(litrosSell > 0 ? [{ section: "Sellador" }, { label: "Litros de sellador", value: `${litrosSell.toFixed(2)} L` }] : []),
      { section: "Complementos" },
      { label: "Herramientas", value: "Rodillo, brocha, charola, cinta, plástico protector" },
      { section: "Costos" },
      { label: "Materiales", value: money(cMat) }, { label: "Instalación / mano de obra", value: money(num(precioInst)) },
      { label: "Total estimado", value: money(total), strong: true },
      ...(areaNeta > 0 ? [{ label: "Costo por m²", value: money(total / areaNeta) }] : []),
    ];
    return { rows, warnings: w, total, inputs: { u, tipo, superficie, manos } };
  }, [u, tipo, superficie, muros, descuentos, manos, rendTeorico, desp, pr, precioInst, selladorManos, selladorRend, prod]);

  return (
    <CalcLayout>
      <FormCol>
        <HowTo title="¿Cómo calcular pintura?"><p>Suma muros/techos, descuenta puertas/ventanas, aplica factor por tipo de superficie, número de manos y desperdicio. El sistema calcula litros y la combinación óptima de envases con menor sobrante.</p></HowTo>
        <Card>
          <Sel label="Unidad" value={u} onChange={setU} opts={UNITS} testid="pin-unidad" />
          <Sel label="Tipo de pintura" value={tipo} onChange={setTipo} opts={["Vinílica", "Acrílica", "Base agua", "Base solvente", "Interiores", "Exteriores", "Fachadas", "Lavable", "Antihongos", "Elastomérica", "Impermeabilizante", "Esmalte", "Barniz", "Epóxica"]} testid="pin-tipo" />
          <Sel label="Tipo de superficie" value={superficie} onChange={setSuperficie} opts={Object.keys(SUP_FACTOR)} testid="pin-superficie" />
          <Sel label="Número de manos" value={manos} onChange={setManos} opts={["1", "2", "3", "4"]} testid="pin-manos" />
        </Card>
        <ProductSelect items={items} value={pid} onChange={setPid} testid="pin-product" />
        <AreaTable title="Superficies (largo × alto)" rows={muros} setRows={setMuros} u={u} defaultName="Muro" testid="pin-muros" />
        <AreaTable title="Descontar (puertas/ventanas)" rows={descuentos} setRows={setDescuentos} u={u} addLabel="+ Descuento" defaultName="Puerta" testid="pin-desc" />
        <Advanced>
          <Field label="Rendimiento teórico (m²/L por mano)" value={rendTeorico} onChange={setRendTeorico} step="0.5" suffix="m²/L" testid="pin-rend" />
          <Field label="Desperdicio (%)" value={desp} onChange={setDesp} step="1" suffix="%" testid="pin-desp" />
          <Field label="Manos de sellador" value={selladorManos} onChange={setSelladorManos} step="1" testid="pin-sellador-manos" />
          <Field label="Rendimiento sellador (m²/L)" value={selladorRend} onChange={setSelladorRend} step="0.5" suffix="m²/L" testid="pin-sellador-rend" />
          <Field label="Precio por litro ($)" value={prod ? prod.price : precioLitro} onChange={setPrecioLitro} suffix="$" testid="pin-precio" />
          <Field label="Mano de obra / instalación ($)" value={precioInst} onChange={setPrecioInst} suffix="$" testid="pin-precio-inst" />
        </Advanced>
      </FormCol>
      <ResultCard title="Cotización de Pintura — GOÛT" modality="pintura" rows={r.rows} warnings={r.warnings} total={r.total} inputs={r.inputs} onSave={onSave} />
    </CalcLayout>
  );
}
